import { Currency } from '../enum/currency'
import { CountryCode } from '../enum/country-code'
import { LanguageCode } from '../enum/language-code'
import { I18nString } from './i18nstring'
import { Period } from '../enum/period'
import { FormArray, FormControl, FormGroup } from '@angular/forms'

export class Utilities {
  static getScale(currency: Currency): number {
    switch (currency) {
      case Currency.SEK:
        return 2
      case Currency.NOK:
        return 2
      case Currency.EUR:
        return 2
      default:
        return 2
    }
  }

  static getWeightAndUnit(
    gram: number,
    decimals: number = 0
  ): { data: string; unit: string } {
    if (gram >= 1000000) {
      return { data: (gram * 0.000001).toFixed(decimals), unit: 'Ton' }
    } else if (gram >= 1000) {
      return { data: (gram * 0.001).toFixed(decimals), unit: 'Kg' }
    } else {
      return { data: gram.toFixed(decimals), unit: 'g' }
    }
  }

  static getPercentage(num: number, decimals: number = 0) {
    return (num * 100).toFixed(decimals)
  }

  static getTaxCents(sum: number, tax: number, scale: number): number {
    return sum - (sum / Math.pow(10, scale) + tax)
  }

  static formatNorwegianDate(date: string) {
    return new Date(
      date.substring(4, 8) +
        '-' +
        date.substring(2, 4) +
        '-' +
        date.substring(0, 2)
    )
  }

  static formatToNorwegianDate(date: string) {
    return (
      date.substring(8, 10) +
      '.' +
      date.substring(5, 7) +
      '.' +
      date.substring(0, 4)
    )
  }

  static formatDateWithAddedYear(
    date: string | number | Date,
    yearsToAdd: number
  ) {
    const newDate = new Date(date)

    newDate.setFullYear(newDate.getFullYear() + yearsToAdd)

    const formattedDate = newDate.toISOString().slice(0, 10)

    return formattedDate
  }

  static toFormattedDate(
    date: string | number | Date,
    expanded: boolean = true
  ) {
    const d = new Date(date)

    const yyyy = d.getFullYear().toString()
    const MM = this.toPrefixedTime(d.getMonth() + 1)
    const dd = this.toPrefixedTime(d.getDate())
    const hh = this.toPrefixedTime(d.getHours())
    const mm = this.toPrefixedTime(d.getMinutes())

    if (!expanded) {
      return yyyy + '-' + MM + '-' + dd
    }
    return yyyy + '-' + MM + '-' + dd + 'T' + hh + ':' + mm
  }

  static toFormatDateOfBirth(
    date: string | number | Date,
    expanded: boolean = false
  ) {
    const inputDate = date.toString()

    if (inputDate.length === 12 && expanded === false) {
      const yyyy = inputDate.slice(0, 4)
      const MM = inputDate.slice(4, 6)
      const dd = inputDate.slice(6, 8)

      return `${yyyy}-${MM}-${dd}`
    } else if (inputDate.length === 12 && expanded === true) {
      const yyyy = inputDate.slice(0, 4)
      const MM = inputDate.slice(4, 6)
      const dd = inputDate.slice(6, 8)
      const XXXX = inputDate.slice(8)

      return `${yyyy}${MM}${dd}-${XXXX}`
    } else if (inputDate.length === 8) {
      const dd = inputDate.slice(0, 2)
      const MM = inputDate.slice(2, 4)
      const yyyy = inputDate.slice(4)

      return `${yyyy}-${MM}-${dd}`
    } else {
      return ''
    }
  }

  static toFormatNid(nid: string): string {
    const inputDate = nid.toString()

    if (inputDate.length === 12) {
      const yyyy = inputDate.slice(0, 4)
      const MM = inputDate.slice(4, 6)
      const dd = inputDate.slice(6, 8)
      const XXXX = inputDate.slice(8)

      return `${yyyy}${MM}${dd}${XXXX}`
    } else if (inputDate.length === 13) {
      const yyyy = inputDate.slice(0, 4)
      const MM = inputDate.slice(4, 6)
      const dd = inputDate.slice(6, 8)
      const XXXX = inputDate.slice(10)

      return `${yyyy}${MM}${dd}${XXXX}`
    } else {
      return nid
    }
  }

  static formatPhoneNumber(phoneNumber: string): string {
    if (phoneNumber.indexOf('+') === -1) {
      return phoneNumber
    } else {
      return phoneNumber.replace('+', '')
    }
  }

  static formatTruncatedCardNumberPreview(cardNumber: string): string {
    return '**** **** **** ' + cardNumber.substring(12, 16)
  }

  static toPrefixedTime(value: number): string {
    return value < 10 ? '0' + value.toString() : value.toString()
  }

  static getFa(control: string, form: FormGroup): FormArray {
    return form.get(control) as FormArray
  }

  static getFc(control: string, form: FormGroup): FormControl {
    return form.get(control) as FormControl
  }

  static getNestedFc(
    control: string,
    form: FormGroup | FormArray,
    index?: number
  ): FormControl {
    if (form instanceof FormArray && index !== undefined) {
      const formGroup = form.at(index) as FormGroup
      return formGroup.get(control) as FormControl
    }
    return form.get(control) as FormControl
  }

  static getFg(control: string, form: FormGroup): FormGroup {
    return form.get(control) as FormGroup
  }

  /**
   * @param digits Length of generated string, defaults to 4.
   * @param numbersOnly return includes only numbers.
   */
  static generateRandomString(
    digits: number = 4,
    numbersOnly: boolean = false
  ) {
    if (digits < 4) {
      throw new Error('Minimum allowed digits is 4.')
    }

    let string = ''

    let set =
      'ABCDEFGHJKLMNOPQRSTUVWXYZ' + 'abcdefghijkmnopqrstuvwxyz0123456789'
    let length = set.length + 1

    if (numbersOnly) {
      set = '0123456789'
      length = set.length
    }

    for (let i = 0; i < digits; i++) {
      const char = Math.floor(Math.random() * length)
      string += set.charAt(char)
    }

    return string
  }

  getEnumValues<T extends { [key: number]: string | number }>(
    e: T
  ): T[keyof T][] {
    return Object.keys(e).filter(
      value => typeof value === 'string'
    ) as T[keyof T][]
  }

  getCurrencyByCountry(country: CountryCode): Currency {
    switch (country) {
      case CountryCode.SE:
        return Currency.SEK
      case CountryCode.NO:
        return Currency.NOK
      case CountryCode.DK:
        return Currency.DKK
      case CountryCode.FI:
        return Currency.EUR
      default:
        return country as unknown as Currency
    }
  }

  getLanguageByCountry(country: CountryCode): LanguageCode {
    switch (country) {
      case CountryCode.SE:
        return LanguageCode.Swedish
      case CountryCode.NO:
        return LanguageCode.Norwegian
      case CountryCode.DK:
        return LanguageCode.Danish
      case CountryCode.FI:
        return LanguageCode.Finnish
      default:
        return LanguageCode.English
    }
  }

  static getCountryByLanguage(language: LanguageCode): CountryCode {
    switch (language) {
      case LanguageCode.Swedish:
        return CountryCode.SE
      case LanguageCode.Norwegian:
        return CountryCode.NO
      case LanguageCode.Danish:
        return CountryCode.DK
      case LanguageCode.Finnish:
        return CountryCode.FI
      case LanguageCode.Spanish:
        return CountryCode.ES
      case LanguageCode.German:
        return CountryCode.DE
      case LanguageCode.French:
        return CountryCode.FR
      default:
        return CountryCode.GB
    }
  }

  static countMonthsToAdd(period: Period): number {
    if (period === Period.Year) {
      return 12
    } else if (period === Period.HalfYear) {
      return 6
    } else if (period === Period.QuarterYear) {
      return 3
    } else if (period === Period.Month) {
      return 1
    }

    return 0
  }

  static getCountryCode(country: string): CountryCode | undefined {
    const countryNames: { [key in CountryCode]: string } = {
      [CountryCode.AL]: 'Albania',
      [CountryCode.AD]: 'Andorra',
      [CountryCode.BE]: 'Belgium',
      [CountryCode.BA]: 'Bosnia and Herzegovina',
      [CountryCode.BG]: 'Bulgaria',
      [CountryCode.CY]: 'Cyprus',
      [CountryCode.DK]: 'Denmark',
      [CountryCode.EE]: 'Estonia',
      [CountryCode.FI]: 'Finland',
      [CountryCode.FR]: 'France',
      [CountryCode.GR]: 'Greece',
      [CountryCode.IE]: 'Ireland',
      [CountryCode.IS]: 'Iceland',
      [CountryCode.IT]: 'Italy',
      [CountryCode.HR]: 'Croatia',
      [CountryCode.LV]: 'Latvia',
      [CountryCode.LI]: 'Liechtenstein',
      [CountryCode.LT]: 'Lithuania',
      [CountryCode.LU]: 'Luxembourg',
      [CountryCode.MT]: 'Malta',
      [CountryCode.MD]: 'Moldova',
      [CountryCode.MC]: 'Monaco',
      [CountryCode.ME]: 'Montenegro',
      [CountryCode.NL]: 'Netherlands',
      [CountryCode.NO]: 'Norway',
      [CountryCode.PL]: 'Poland',
      [CountryCode.PT]: 'Portugal',
      [CountryCode.RO]: 'Romania',
      [CountryCode.RU]: 'Russia',
      [CountryCode.SM]: 'San Marino',
      [CountryCode.CH]: 'Switzerland',
      [CountryCode.RS]: 'Serbia',
      [CountryCode.SK]: 'Slovakia',
      [CountryCode.SI]: 'Slovenia',
      [CountryCode.ES]: 'Spain',
      [CountryCode.GB]: 'United Kingdom',
      [CountryCode.SE]: 'Sweden',
      [CountryCode.CZ]: 'Czech Republic',
      [CountryCode.TR]: 'Turkey',
      [CountryCode.DE]: 'Germany',
      [CountryCode.UA]: 'Ukraine',
      [CountryCode.HU]: 'Hungary',
      [CountryCode.VA]: 'Vatican City',
      [CountryCode.BY]: 'Belarus',
      [CountryCode.AT]: 'Austria',
    }

    const countryEntries = Object.entries(countryNames)
    for (const [code, name] of countryEntries) {
      if (name.toLowerCase() === country.toLowerCase()) {
        return code as CountryCode
      }
    }
    return undefined
  }

  static getCountryName(code: CountryCode): string {
    const countryNames: { [key in CountryCode]: string } = {
      [CountryCode.AL]: 'Albania',
      [CountryCode.AD]: 'Andorra',
      [CountryCode.BE]: 'Belgium',
      [CountryCode.BA]: 'Bosnia and Herzegovina',
      [CountryCode.BG]: 'Bulgaria',
      [CountryCode.CY]: 'Cyprus',
      [CountryCode.DK]: 'Denmark',
      [CountryCode.EE]: 'Estonia',
      [CountryCode.FI]: 'Finland',
      [CountryCode.FR]: 'France',
      [CountryCode.GR]: 'Greece',
      [CountryCode.IE]: 'Ireland',
      [CountryCode.IS]: 'Iceland',
      [CountryCode.IT]: 'Italy',
      [CountryCode.HR]: 'Croatia',
      [CountryCode.LV]: 'Latvia',
      [CountryCode.LI]: 'Liechtenstein',
      [CountryCode.LT]: 'Lithuania',
      [CountryCode.LU]: 'Luxembourg',
      [CountryCode.MT]: 'Malta',
      [CountryCode.MD]: 'Moldova',
      [CountryCode.MC]: 'Monaco',
      [CountryCode.ME]: 'Montenegro',
      [CountryCode.NL]: 'Netherlands',
      [CountryCode.NO]: 'Norway',
      [CountryCode.PL]: 'Poland',
      [CountryCode.PT]: 'Portugal',
      [CountryCode.RO]: 'Romania',
      [CountryCode.RU]: 'Russia',
      [CountryCode.SM]: 'San Marino',
      [CountryCode.CH]: 'Switzerland',
      [CountryCode.RS]: 'Serbia',
      [CountryCode.SK]: 'Slovakia',
      [CountryCode.SI]: 'Slovenia',
      [CountryCode.ES]: 'Spain',
      [CountryCode.GB]: 'United Kingdom',
      [CountryCode.SE]: 'Sweden',
      [CountryCode.CZ]: 'Czech Republic',
      [CountryCode.TR]: 'Turkey',
      [CountryCode.DE]: 'Germany',
      [CountryCode.UA]: 'Ukraine',
      [CountryCode.HU]: 'Hungary',
      [CountryCode.VA]: 'Vatican City',
      [CountryCode.BY]: 'Belarus',
      [CountryCode.AT]: 'Austria',
    }

    return countryNames[code]
  }

  static parseI18n(i18n: I18nString[], languageCode: LanguageCode): string {
    if (!i18n || i18n.length === 0) {
      throw new Error('i18n is empty')
    }
    let i = i18n.findIndex(i => {
      return i.language === languageCode
    })
    if (i < 0) {
      i = i18n.findIndex(i => {
        return i.language === LanguageCode.English
      })
    }
    if (i < 0) {
      return i18n[0].value
    } else {
      return i18n[i].value
    }
  }

  static isArrayI18n(arr: any): arr is I18nString[] {
    return Array.isArray(arr) && arr.every(item => item instanceof I18nString)
  }

}

declare global {
  interface Date {
    getWeek(): number
    addDays(days: number): Date
  }
}

Date.prototype.getWeek = function (): number {
  const date = new Date(this.getTime())
  date.setHours(0, 0, 0, 0)

  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))

  const week1 = new Date(date.getFullYear(), 0, 4)

  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    )
  )
}

Date.prototype.addDays = function (days: number): Date {
  const date = new Date(this.valueOf())
  date.setDate(date.getDate() + days)
  return date
}
